import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';
import type { Page } from '../../types/api';

export const AdminPagesPage: React.FC = () => {
    const { setFlashMessage } = useOutletContext<{ setFlashMessage: (msg: any) => void }>();
    const [pages, setPages] = useState<Page[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [editingPage, setEditingPage] = useState<Partial<Page> | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    const loadPages = async () => {
        setLoading(true);
        try {
            const res = await api.adminGetPages();
            setPages(res.data);
        } catch {
            // error handled
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPages();
    }, []);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingPage) return;
        setSaving(true);
        try {
            await api.adminSavePage(editingPage.id || null, editingPage);
            setFlashMessage({
                type: 'success',
                text: `Page successfully ${editingPage.id ? 'updated' : 'created'}.`
            });
            setEditingPage(null);
            loadPages();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to save page.' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete page permanently?')) return;
        try {
            await api.adminDeletePage(id);
            setFlashMessage({ type: 'success', text: 'Page deleted successfully.' });
            loadPages();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to delete page.' });
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">Pages & CMS Builder</h1>
                    <p className="text-sm text-slate-400 mt-1">Manage static and dynamic pages with custom SEO and section structures.</p>
                </div>

                <button 
                    type="button" 
                    onClick={() => setEditingPage({ title: '', slug: '', is_published: true, sort_order: 0 })}
                    className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg transition cursor-pointer"
                >
                    + Create Page
                </button>
            </div>

            {/* Modal for editing / creating page */}
            {editingPage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full space-y-6 shadow-2xl">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold text-white">
                                {editingPage.id ? 'Edit Page' : 'Create New Page'}
                            </h2>
                            <button 
                                type="button" 
                                onClick={() => setEditingPage(null)}
                                className="text-slate-400 hover:text-white text-xl"
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Page Title</label>
                                <input 
                                    type="text" 
                                    required
                                    value={editingPage.title || ''}
                                    onChange={e => {
                                        const title = e.target.value;
                                        if (!editingPage.id) {
                                            const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                                            setEditingPage({ ...editingPage, title, slug });
                                        } else {
                                            setEditingPage({ ...editingPage, title });
                                        }
                                    }}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Slug</label>
                                <input 
                                    type="text" 
                                    required
                                    value={editingPage.slug || ''}
                                    onChange={e => setEditingPage({ ...editingPage, slug: e.target.value })}
                                    className="w-full px-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Meta Title</label>
                                <input 
                                    type="text" 
                                    value={editingPage.meta_title || ''}
                                    onChange={e => setEditingPage({ ...editingPage, meta_title: e.target.value })}
                                    className="w-full px-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Meta Description</label>
                                <textarea 
                                    rows={2}
                                    value={editingPage.meta_description || ''}
                                    onChange={e => setEditingPage({ ...editingPage, meta_description: e.target.value })}
                                    className="w-full px-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                />
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={editingPage.is_published ?? true}
                                        onChange={e => setEditingPage({ ...editingPage, is_published: e.target.checked })}
                                        className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500" 
                                    />
                                    <span>Page is active and published</span>
                                </label>
                            </div>

                            <div className="pt-4 border-t border-slate-800 flex items-center justify-end gap-3">
                                <button 
                                    type="button" 
                                    onClick={() => setEditingPage(null)}
                                    className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={saving}
                                    className="px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'Save Page'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Pages Table */}
            <div className="p-6 rounded-3xl glass-card overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="border-b border-slate-800 text-xs text-slate-400 uppercase tracking-wider">
                            <th className="py-4 font-semibold">Title</th>
                            <th className="py-4 font-semibold">URL Path</th>
                            <th className="py-4 font-semibold">Sections</th>
                            <th className="py-4 font-semibold">Status</th>
                            <th className="py-4 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {pages.length > 0 ? (
                            pages.map(page => (
                                <tr key={page.id} className="hover:bg-slate-800/20">
                                    <td className="py-4 font-bold text-white">{page.title}</td>
                                    <td className="py-4 font-mono text-xs text-emerald-400">/{page.slug}</td>
                                    <td className="py-4 text-xs text-slate-400">{page.sections_count || page.sections?.length || 0} sections</td>
                                    <td className="py-4">
                                        {page.is_published ? (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                Active
                                            </span>
                                        ) : (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-800 text-slate-400 border border-slate-700">
                                                Inactive
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-4 text-right space-x-2">
                                        <button 
                                            type="button" 
                                            onClick={() => setEditingPage(page)}
                                            className="px-2.5 py-1 rounded text-xs bg-emerald-950 text-emerald-300 border border-emerald-800 hover:bg-emerald-900 transition cursor-pointer"
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={() => handleDelete(page.id)}
                                            className="px-2.5 py-1 rounded text-xs bg-rose-950/60 text-rose-400 border border-rose-800/40 hover:bg-rose-900 transition cursor-pointer"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-slate-500">
                                    {loading ? 'Loading pages...' : 'No pages found.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
